import Interaction from "../../../models/interactions/Interaction";
import {
  ITransition,
  ITypewriterTransitionSettings,
  TransitionType,
} from "../../../models/interactions/Transition";
import {
  normalizeTransition,
  withTransitionType,
} from "../../../models/interactions/TransitionDefaults";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import PiColorPickerField from "../../../components/PiColorPickerField";
import PiAssetSelect from "../../../components/PiAssetSelect";
import PiInputfield from "../../../components/PiInputField";
import PiSegmentedToggle from "../../../components/PiSegmentedToggle";
import PiSwitch from "../../../components/PiSwitch";
import SequenceLocalizedVariableInputField from "../components/SequenceLocalizedVariableInputField";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";

export default function TransitionNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const transition = normalizeTransition(props.interaction as ITransition);
  const { updateInteraction } = useUpdateSequenceInteraction();
  const { GetArrayOfKeys } = useTrainingLocalization();

  const parseNumber = (value: string, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const updateTypewriter = (patch: Partial<ITypewriterTransitionSettings>) =>
    updateInteraction(props.nodeId, {
      ...transition,
      typewriter: { ...transition.typewriter, ...patch },
    } as ITransition);

  const onChangeTypewriterNumber =
    (
      field:
        | "fontSize"
        | "transitionSpeedStart"
        | "transitionSpeedStop"
        | "typingSpeed"
        | "showButtonDelay"
    ) =>
    (raw: string) =>
      updateTypewriter({
        [field]: parseNumber(raw, transition.typewriter[field]),
      });

  const typeToggle = (
    <PiSegmentedToggle<TransitionType>
      ariaLabel="Transition type"
      fullWidth
      value={transition.transitionType}
      onChange={(type) =>
        updateInteraction(
          props.nodeId,
          withTransitionType(transition, type, GetArrayOfKeys)
        )
      }
      options={[
        {
          label: "Default",
          value: "default",
          testId: "node-transition-type-default",
        },
        {
          label: "Typewriter",
          value: "typewriter",
          testId: "node-transition-type-typewriter",
        },
      ]}
    />
  );

  const defaultEditor = (
    <>
      <PiColorPickerField
        label="Color"
        color={transition.color}
        onChange={(color) =>
          updateInteraction(props.nodeId, {
            ...transition,
            color: color,
          } as ITransition)
        }
      />
      <SequenceLocalizedVariableInputField
        label="Text"
        placeholder="Text"
        localizationKey={transition.text}
      />
      <PiAssetSelect
        label="Icon"
        assetId={transition.assetId}
        onChange={(assetId: string) =>
          updateInteraction(props.nodeId, {
            ...transition,
            assetId: assetId,
          } as ITransition)
        }
      />
      <PiInputfield
        label="Duration"
        placeholder="Duration"
        value={transition.duration.toString()}
        onChange={(duration: string) =>
          updateInteraction(props.nodeId, {
            ...transition,
            duration: parseNumber(duration, transition.duration),
          } as ITransition)
        }
      />
    </>
  );

  const typewriter = transition.typewriter;

  const typewriterEditor = (
    <>
      <SequenceLocalizedVariableInputField
        label="Title"
        placeholder="Title"
        localizationKey={typewriter.title}
        minRows={1}
        disabled={typewriter.title === ""}
      />
      <SequenceLocalizedVariableInputField
        label="Text"
        placeholder="Text"
        localizationKey={typewriter.text}
        disabled={typewriter.text === ""}
      />
      <PiInputfield
        label="Font size"
        placeholder="Font size"
        value={typewriter.fontSize.toString()}
        onChange={onChangeTypewriterNumber("fontSize")}
      />
      <PiSwitch
        label="Instant text"
        checked={typewriter.instantText}
        onChange={(instantText: boolean) => updateTypewriter({ instantText })}
      />
      <PiSwitch
        label="Use background color"
        checked={typewriter.useBackgroundColor}
        onChange={(useBackgroundColor: boolean) =>
          updateTypewriter({ useBackgroundColor })
        }
      />
      <PiColorPickerField
        label="Background color"
        color={typewriter.backgroundColor}
        onChange={(backgroundColor) =>
          updateTypewriter({ backgroundColor })
        }
      />
      <PiSwitch
        label="Use text color"
        checked={typewriter.useTextColor}
        onChange={(useTextColor: boolean) =>
          updateTypewriter({ useTextColor })
        }
      />
      <PiColorPickerField
        label="Text color"
        color={typewriter.textColor}
        onChange={(textColor) => updateTypewriter({ textColor })}
      />
      <PiAssetSelect
        label="Title icon"
        assetId={typewriter.titleIconAssetId}
        onChange={(titleIconAssetId: string) =>
          updateTypewriter({ titleIconAssetId })
        }
      />
      <PiAssetSelect
        label="Bottom icon"
        assetId={typewriter.bottomIconAssetId}
        onChange={(bottomIconAssetId: string) =>
          updateTypewriter({ bottomIconAssetId })
        }
      />
      <PiInputfield
        label="Transition speed start"
        placeholder="Transition speed start"
        value={typewriter.transitionSpeedStart.toString()}
        onChange={onChangeTypewriterNumber("transitionSpeedStart")}
      />
      <PiInputfield
        label="Transition speed stop"
        placeholder="Transition speed stop"
        value={typewriter.transitionSpeedStop.toString()}
        onChange={onChangeTypewriterNumber("transitionSpeedStop")}
      />
      <PiInputfield
        label="Typing speed"
        placeholder="Typing speed"
        value={typewriter.typingSpeed.toString()}
        onChange={onChangeTypewriterNumber("typingSpeed")}
      />
      <PiInputfield
        label="Show button delay"
        placeholder="Show button delay"
        value={typewriter.showButtonDelay.toString()}
        onChange={onChangeTypewriterNumber("showButtonDelay")}
      />
    </>
  );

  return (
    <div className="w-full h-full flex flex-col gap-[8px]">
      {typeToggle}
      {transition.transitionType === "typewriter"
        ? typewriterEditor
        : defaultEditor}
    </div>
  );
}
